import { FC, useEffect } from 'react';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { PHRASE_TYPES, useUpdateConversationPhrase } from '@/entities/conversations';
import { buildRecordFormValues, buildUpdateRequest } from './recordForm';
import { useTranscriptEditorContext } from './TranscriptEditorContext';
import { TranscriptRecordForm } from './TranscriptRecordForm';
import { TranscriptRecordFormFooter } from './TranscriptRecordFormFooter';
import { TranscriptRecordFormFrame } from './TranscriptRecordFormFrame';
import { TranscriptSplitPhraseForm } from './TranscriptSplitPhraseForm';
import type { EditableTranscriptRecord } from './types';
import { useTranscriptPhraseSplit } from './useTranscriptPhraseSplit';
import { useTranscriptRecordForm } from './useTranscriptRecordForm';

type Props = {
  record: EditableTranscriptRecord;
};

/**
 * Правка существующей записи на её месте в ленте: сохраняются только изменённые поля. Вынесенный из реплики
 * фрагмент правится новой репликой под ней, и пара сохраняется или отменяется целиком.
 */
export const TranscriptRecordEditForm: FC<Props> = ({ record }) => {
  const { t } = useTranslation(['dialogs']);
  const { conversationId, closeForm, reportDirty } = useTranscriptEditorContext();
  const { mutate: updatePhrase, isPending } = useUpdateConversationPhrase();
  const form = useTranscriptRecordForm(record.type, buildRecordFormValues(record));
  const personId = useWatch({ control: form.control, name: 'personId' });
  const split = useTranscriptPhraseSplit(record.id, form);

  // Вынесенный фрагмент — несохранённая правка, даже если текст исходной вернули к прежнему
  const isDirty = form.formState.isDirty || split.isSplitOpen;
  useEffect(() => {
    reportDirty(isDirty);
  }, [isDirty, reportDirty]);

  const saveRecord = form.handleSubmit((values) => {
    const data = buildUpdateRequest(record.type, values, form.formState.dirtyFields);
    if (Object.keys(data).length === 0) {
      closeForm();
      return;
    }
    updatePhrase({ conversationId, phraseId: record.id, data }, { onSuccess: closeForm });
  });
  const handleSave = split.isSplitOpen ? split.saveSplit : () => void saveRecord();

  const footer = (
    <TranscriptRecordFormFooter
      isDirty={isDirty}
      isSaving={isPending || split.isSaving}
      onSave={handleSave}
      onCancel={closeForm}
    />
  );

  return (
    <TranscriptRecordFormFrame type={record.type} personId={personId} recordId={record.id}>
      <div onFocusCapture={() => split.setTimelineTarget('source')}>
        <TranscriptRecordForm
          form={form}
          type={record.type}
          title={t(`dialogs:transcriptEdit.editTitle.${record.type}`)}
          isTimelineTarget={!split.isSplitOpen || split.timelineTarget === 'source'}
          onSubmit={handleSave}
          onSplit={record.type === PHRASE_TYPES.PHRASE ? split.openSplit : undefined}
          isSplitOpen={split.isSplitOpen}
        >
          {!split.isSplitOpen && footer}
        </TranscriptRecordForm>
      </div>
      {split.isSplitOpen && (
        <>
          <TranscriptSplitPhraseForm
            form={split.splitForm}
            isTimelineTarget={split.timelineTarget === 'split'}
            onSubmit={handleSave}
            onFocus={() => split.setTimelineTarget('split')}
          />
          {footer}
        </>
      )}
    </TranscriptRecordFormFrame>
  );
};
